import { useEffect, useState } from "react";
import useTitleStore from "../../store/titleStore";
import useDebounce from "../../hooks/useDebounce";
import { useTableHeight } from "../../hooks/useTableHeight";
import { useNavigate } from "react-router";
import { Button, Card, Flex, Form, Grid, Input, Pagination, Select, Table } from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { surveyService } from "../../services/survey.service";
import { indonesiaRegionService } from "../../services/indonesia-region.service";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import RenderIf from "../../components/RenderIf";
import DocumentAnalysisResponsiveFilter from "../../components/DocumentAnalysisResponsiveFilter";

export default function CanceledDocumentAnalysis(){

  const breakpoint = Grid.useBreakpoint()

  const navigate = useNavigate();

  const { setTitle } = useTitleStore();

  const tableHeight = useTableHeight();

  const [responsiveFilter, setResponsiveFilter] = useState(false)

  const [keyword, setKeyword] = useState("");
  const [marketing, setMarketing] = useState("");
  const [selectedCity, setSelectedCity] = useState('')
  const [selectedDistrict, setSelectedDistrict] = useState('')
  const debouncedKeyword = useDebounce(keyword, 500);
  const debouncedMarketing = useDebounce(marketing, 500);

  const [marketingQuery, setMarketingQuery] = useLocalStorage('surveyorQuery', debouncedMarketing);

  const [filterForm] = Form.useForm()

  const city = Form.useWatch('city', filterForm)
  const district = Form.useWatch('district', filterForm)
  const village = Form.useWatch('village', filterForm)

  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: []
  })

  useEffect(() => {
    setTitle("Analisis Berkas - Data Tercancel");
    setMarketing(marketingQuery)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams({
      ...dataParams,
      filter: [
        {
          operator: 'orWhere',
          value: [
            {
              field: 'surveyor',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'tracking_id',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'nama',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'telepon',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'rt',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'rw',
              operator: 'like',
              value: debouncedKeyword
            },
          ]
        },
        {
          field: 'surveyor',
          operator: 'like',
          value: debouncedMarketing
        },
        {
          field: 'kota',
          operator: 'like',
          value: city
        },
        {
          field: 'kecamatan',
          operator: 'like',
          value: district
        },
        {
          field: 'kelurahan',
          operator: 'like',
          value: village
        },
      ]
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword, city, district, village, debouncedMarketing]);


  const { data: surveyData , isLoading, isRefetching } = useQuery({
    queryKey: ['registration', dataParams],
    queryFn: () => surveyService.getRejectedData(dataParams),
    placeholderData: keepPreviousData,
  })

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const handleChangeMarketingQuery = (q) => {
    setMarketingQuery(q)
    setMarketing(q)
  }

  const { data: cities } = useQuery({
    queryKey: ['cities'],
    queryFn: () => indonesiaRegionService.getCities()
  })

  const { data: districts } = useQuery({
    queryKey: ['districts', selectedCity],
    queryFn: () => indonesiaRegionService.getDistricts(selectedCity),
    enabled: !!selectedCity
  })

  const { data: villages } = useQuery({
    queryKey: ['districts', selectedDistrict],
    queryFn: () => indonesiaRegionService.getSubdistricts(selectedDistrict),
    enabled: !!selectedDistrict
  })

  useEffect(() => {

    filterForm.resetFields(['district', 'village'])

    if(city){
      const selected = cities.find(e => e.name == city)
      setSelectedCity(selected.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city])

  useEffect(() => {

    filterForm.resetFields(['village'])
    
    if(district){
      const selected = districts.find(e => e.name == district)
      setSelectedDistrict(selected.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [district])


  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      fixed: breakpoint.md && 'left',
      render: (text, record, index) =>  index + 1,
    },
    {
      title: 'Tracking ID',
      dataIndex: 'acceptance',
      width: 150,
      fixed: breakpoint.md && 'left',
      render: val => val?.registration?.tracking_id
    },
    {
      title: 'Nama Pelanggan',
      dataIndex: 'nama',
      width: 180
    },
    {
      title: 'No Telp',
      dataIndex: 'telepon',
      width: 230
    },
    { 
      title: 'Kota',
      dataIndex: 'kota',
      width: 180,
    },
    { 
      title: 'Kelurahan',
      dataIndex: 'kelurahan',
      width: 150,
    },
    { 
      title: 'RT/RW',
      width: 120,
      render: val => `${val.rt?.toString().padStart(3, '0')}/${val.rw?.toString().padStart(3, '0')}`
    },
    {
      title: 'Surveyor',
      dataIndex: 'acceptance',
      width: 150,
      render: val => val?.surveyor?.name
    },
    {
      title: 'Marketing',
      dataIndex: 'acceptance',
      width: 150,
      render: val => val?.surveyor?.marketing?.name
    },
    {
      title: 'Di Cancel Oleh',
      dataIndex: 'rejection',
      width: 150,
      render: val => val?.rejectedBy?.name
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 80,
      fixed: 'right',
      render: (record) => (
        <Button  
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="primary"
          variant="filled"
          onClick={() => navigate('/document-analysis/canceled/detail/'+record.id)} 
          size=""
        />
      )
    },
  ]

  return (
    <>
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <RenderIf when={breakpoint.lg} >
          <Form form={filterForm} >
            <Flex justify="flex-start" gap={20} style={{ paddingInline: 24, marginBottom: 24 }} >
              <Input 
                style={{ width: 250 }} 
                prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
                placeholder="cari.."
                onChange={e => setKeyword(e.target.value)}
                value={keyword}
              />
              <Input
                prefix={<Icon icon="solar:user-rounded-linear" width="16" height="16" />}
                style={{ width: 220 }}
                placeholder="Marketing"
                onChange={e => handleChangeMarketingQuery(e.target.value)}
                value={marketing}
                allowClear
              />
              <Form.Item noStyle name={'city'} >
                <Select
                  allowClear
                  options={cities?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kota"
                />
              </Form.Item>
              <Form.Item noStyle name={'district'} >
                <Select
                  allowClear
                  options={districts?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kecamatan"
                  disabled={!city}
                />
              </Form.Item>
              <Form.Item name={'village'} noStyle >
                <Select
                  allowClear
                  options={villages?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kelurahan"
                  disabled={!district}
                />
              </Form.Item>
            </Flex>
          </Form>
        </RenderIf>
        <RenderIf when={!breakpoint.lg} >
          <Flex justify="flex-end" gap={20} style={{ padding: '0 24px', marginBottom: 24 }} >
            <Button 
              variant="outlined" 
              color="primary" 
              icon={<Icon icon="mynaui:filter-solid" width="22" height="22" />}
              onClick={() => setResponsiveFilter(true)}
            >
              Filter
            </Button>
          </Flex>
        </RenderIf>
        <Table
          id="registration-table"
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={surveyData?.data}
          pagination={false}
          rowClassName={(record) => !record.is_open ? 'row-open' : ''}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={surveyData?.pagination?.total}
          showSizeChanger
          pageSizeOptions={['10', '20', '50']}
          current={surveyData?.pagination?.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
      <DocumentAnalysisResponsiveFilter
        form={filterForm}
        setKeyword={setKeyword}
        keyword={keyword}
        city={city}
        cities={cities}
        responsiveFilter={responsiveFilter}
        setResponsiveFilter={setResponsiveFilter}
        district={district}
        districts={districts}
        villages={villages}
        handleChangeMarketingQuery={handleChangeMarketingQuery}
        marketing={marketing}
      />
    </>
  )
}